import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MiniGameService } from '../../../_services/mini-game.service';
import { GameAudioService } from '../../../_services/game-audio.service';
import { ImmersiveGameBase } from '../_shared/immersive-game.base';
import { GlobalService } from 'src/app/_services/global.service';
import { MiniGameSession, MiniGameStudent } from '../../../interfaces/mini-game.interface';
import { SymmetryShape, SymmetryState } from '../../../interfaces/symmetry.interface';

/**
 * Host shell for Mirror Match — the lines-of-symmetry skill game.
 *
 * Composition mirrors the other Game-World mini-games: the actual game
 * state (`SymmetryState`) lives in `MiniGameService.session$`; this
 * component only **reads** it (via the `state` / `shape` / `student`
 * getters) and writes outcomes back through
 * `scoreSymmetryRound` + `advanceSymmetryRound`.
 *
 * The interactive surface is delegated to `<app-symmetry-stage>`, which
 * emits `solved` / `missed`. This shell owns everything *around* the
 * play area — the prompt bar, whose-turn chip, round progress, hint /
 * skip controls, and the win / retry feedback — the reusable "skill
 * game chrome" a sibling subject game would keep while swapping the
 * stage.
 */
@Component({
  selector: 'app-symmetry-game',
  templateUrl: './symmetry-game.component.html',
  styleUrls: ['./symmetry-game.component.scss']
})
export class SymmetryGameComponent extends ImmersiveGameBase implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  currLang: any = 'en';

  session: MiniGameSession | null = null;

  // Per-round UI state (reset whenever the round index changes).
  attempts = 0;
  hintVisible = false;
  showSuccess = false;
  resolving = false;
  lastXp = 0;

  private prevRound = -1;
  private pendingTimeouts: any[] = [];

  constructor(
    private miniGameService: MiniGameService,
    private gameAudioService: GameAudioService,
    private globalService: GlobalService
  ) {
    super();
  }

  ngOnInit(): void {
    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => this.currLang = lang);

    this.miniGameService.session$
      .pipe(takeUntil(this.destroy$))
      .subscribe(s => {
        this.session = s;
        const round = this.state?.currentRound ?? -1;
        if (round !== this.prevRound) {
          this.prevRound = round;
          this.resetRoundUi();
        }
      });

    this.miniGameService.lastResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe(r => { if (r) this.lastXp = r.xpEarned; });
  }

  ngOnDestroy(): void {
    this.pendingTimeouts.forEach(t => clearTimeout(t));
    this.pendingTimeouts = [];
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resetRoundUi(): void {
    this.attempts = 0;
    this.hintVisible = false;
    this.showSuccess = false;
    this.resolving = false;
  }

  private later(fn: () => void, ms: number): void {
    this.pendingTimeouts.push(setTimeout(fn, ms));
  }

  // ── Derived state ──────────────────────────────────────────────────

  get state(): SymmetryState | null {
    return (this.session?.gameState as SymmetryState) ?? null;
  }

  get shape(): SymmetryShape | null {
    const s = this.state;
    if (!s) return null;
    return s.rounds[s.currentRound] ?? null;
  }

  get currentRound(): number {
    return this.state?.currentRound ?? 0;
  }

  get totalRounds(): number {
    return this.state?.totalRounds ?? 0;
  }

  get roundDots(): number[] {
    return Array.from({ length: this.totalRounds }, (_, i) => i);
  }

  get solvedFlags(): boolean[] {
    return this.state?.solved ?? [];
  }

  get student(): MiniGameStudent | null {
    const s = this.state;
    if (!s || s.currentStudentId == null) return null;
    return this.session?.students.find(st => st.id === s.currentStudentId) ?? null;
  }

  get studentName(): string {
    const st = this.student;
    if (!st) return '';
    const full = this.currLang === 'ar' ? st.name.ar : st.name.en;
    return (full || '').split(' ')[0];
  }

  get shapeName(): string {
    const sh = this.shape;
    if (!sh) return '';
    return this.currLang === 'ar' ? sh.name.ar : sh.name.en;
  }

  get axisHintLabel(): string {
    const n = this.shape?.axisCount ?? 0;
    if (this.currLang === 'ar') {
      return n === 1 ? 'خط تماثل واحد' : `${n} خطوط تماثل`;
    }
    return n === 1 ? '1 line of symmetry' : `${n} lines of symmetry`;
  }

  /** Hint becomes offerable after two honest attempts. */
  get canUseHint(): boolean {
    return !this.hintVisible && !this.resolving && this.attempts >= 2;
  }

  // ── Stage events ───────────────────────────────────────────────────

  onSolved(): void {
    if (this.resolving) return;
    this.resolving = true;
    this.playSuccessSound();
    // Record first so lastResult$ carries the XP into the banner.
    this.miniGameService.scoreSymmetryRound(true);
    this.showSuccess = true;
    this.later(() => this.miniGameService.advanceSymmetryRound(), 1800);
  }

  /**
   * A wrong release is intentionally silent — no sound, no "wrong"
   * flash. The child is mid-exploration and will drag again, so
   * punishing every attempt just nags. We only tally tries quietly so
   * the Hint can surface after a couple of honest goes.
   */
  onMissed(): void {
    if (this.resolving) return;
    this.attempts++;
  }

  useHint(): void {
    this.hintVisible = true;
  }

  skip(): void {
    if (this.resolving) return;
    this.resolving = true;
    this.miniGameService.scoreSymmetryRound(false);
    this.later(() => this.miniGameService.advanceSymmetryRound(), 250);
  }

  private playSuccessSound(): void {
    const audio = this.gameAudioService.play('assets/game-mode/sound-effects/applauseSoundEffect.mp3');
    if (audio) this.later(() => this.gameAudioService.stop(audio), 2500);
  }
}
